import { memo, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Text } from '@fly/core';
import { useVirtualizer } from '@tanstack/react-virtual';

import { useGetArtifactFilesInfiniteQuery } from '../../../../store/registry.service';
import { FileRow } from './FileRow';
import { FileSkeleton } from './FileSkeleton';

const MAX_VISIBLE_FILES = 6;
const FILE_ROW_HEIGHT_PX = 24;
const FILE_ROW_GAP_PX = 4;
const LOAD_MORE_THRESHOLD_ROWS = 3;
const LOAD_MORE_THRESHOLD_PX =
  LOAD_MORE_THRESHOLD_ROWS * (FILE_ROW_HEIGHT_PX + FILE_ROW_GAP_PX);
const LOAD_MORE_SKELETON_COUNT = 2;
const MAX_LIST_HEIGHT_PX =
  MAX_VISIBLE_FILES * FILE_ROW_HEIGHT_PX +
  (MAX_VISIBLE_FILES - 1) * FILE_ROW_GAP_PX;

export function BaseArtifactFiles({
  name,
  packageVersion,
}: {
  name: string;
  packageVersion: string;
}) {
  const { t } = useTranslation();
  const scrollContainerRef = useRef<HTMLUListElement>(null);
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetArtifactFilesInfiniteQuery({
    name,
    packageVersion,
  });

  const files = useMemo(
    () => data?.pages.flatMap((page) => page.files) ?? [],
    [data]
  );

  const rowVirtualizer = useVirtualizer({
    count: files.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => FILE_ROW_HEIGHT_PX,
    gap: FILE_ROW_GAP_PX,
    overscan: 3,
  });

  const handleScroll = useCallback(() => {
    if (!hasNextPage || isLoading || isFetchingNextPage) return;

    const element = scrollContainerRef.current;
    if (!element) return;

    const { scrollHeight, scrollTop, clientHeight } = element;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    if (distanceFromBottom < LOAD_MORE_THRESHOLD_PX) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isLoading]);

  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();
  const loadingMoreAreaHeight = isFetchingNextPage
    ? LOAD_MORE_SKELETON_COUNT * (FILE_ROW_HEIGHT_PX + FILE_ROW_GAP_PX)
    : 0;

  let content: React.ReactNode = (
    <>
      <li
        aria-hidden
        className='m-0 p-0 invisible list-none border-0'
        style={{ height: `${totalSize + loadingMoreAreaHeight}px` }}
      />
      {virtualItems.map((virtualItem) => {
        const file = files[virtualItem.index];
        if (!file) return null;

        return (
          <li
            key={file.sha256 ?? `${file.fileName}::${virtualItem.start}`}
            className='top-0 left-0 h-6 absolute flex w-full list-none items-center gap-fly-2'
            style={{
              transform: `translateY(${virtualItem.start}px)`,
            }}
          >
            <FileRow file={file} />
          </li>
        );
      })}
      {isFetchingNextPage &&
        Array.from({ length: LOAD_MORE_SKELETON_COUNT }, (_, index) => (
          <FileSkeleton
            key={`artifact-files-loading-${index}`}
            className='top-0 left-0 absolute w-full list-none'
            style={{
              transform: `translateY(${totalSize + index * (FILE_ROW_HEIGHT_PX + FILE_ROW_GAP_PX)}px)`,
            }}
          />
        ))}
    </>
  );

  if (files.length === 0) {
    content = (
      <li className='list-none'>
        <Text size='2' color='neutral' weight='medium'>
          {t('app.common.not_found.none_found')}
        </Text>
      </li>
    );
  }

  if (isLoading) {
    content = <FileSkeleton />;
  }

  if (error) {
    content = (
      <li className='list-none'>
        <Text size='2' color='error' weight='medium'>
          {t('app.common.errors.global_error', {
            defaultValue: 'An error occurred',
          })}
        </Text>
      </li>
    );
  }

  return (
    <ul
      ref={scrollContainerRef}
      className='relative mb-fly-3 ml-[46px] list-none overflow-y-auto pr-fly-6'
      style={{ maxHeight: `${MAX_LIST_HEIGHT_PX}px` }}
      onScroll={handleScroll}
    >
      {content}
    </ul>
  );
}

export const ArtifactFiles = memo(BaseArtifactFiles);
